import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Switch,
  Tooltip,
  InputAdornment,
  FormGroup,
  Divider,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

const PROVIDERS = {
  elevenlabs: { name: 'ElevenLabs', color: '#1a1a1a', cost: 0.18 },
  google: { name: 'Google Cloud', color: '#4285f4', cost: 0.016 },
  azure: { name: 'Azure', color: '#0078d4', cost: 0.016 },
  openai: { name: 'OpenAI', color: '#10a37f', cost: 0.015 }
};

const ProviderPermissionsManager = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [therapists, setTherapists] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTherapists, setSelectedTherapists] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    allowedProviders: [],
    canSelectProvider: false,
    costLimit: { daily: 10, monthly: 200 },
    overrideInterfaceProvider: false
  });

  // Bulk edit form state
  const [bulkEditForm, setBulkEditForm] = useState({
    allowedProviders: ['elevenlabs', 'google'],
    canSelectProvider: false,
    costLimit: { daily: 10, monthly: 200 },
    overrideInterfaceProvider: false
  });

  useEffect(() => {
    fetchTherapistPermissions();
    fetchUsageStats();
  }, []);

  const fetchTherapistPermissions = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await axios.get(
        `${API_BASE_URL}/admin/provider-permissions`,
        { headers: { 'x-auth-token': token } }
      );
      setTherapists(response.data.therapists || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to load therapist permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await axios.get(
        `${API_BASE_URL}/admin/provider-permissions/usage-stats`,
        { headers: { 'x-auth-token': token } }
      );
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching usage stats:', err);
    }
  };

  const handleEditTherapist = (therapist) => {
    setSelectedTherapist(therapist);
    setEditForm({
      allowedProviders: therapist.allowedProviders || [],
      canSelectProvider: therapist.providerPermissions?.canSelectProvider || false,
      costLimit: therapist.providerPermissions?.costLimit || { daily: 10, monthly: 200 },
      overrideInterfaceProvider: therapist.providerPermissions?.overrideInterfaceProvider || false
    });
    setEditDialogOpen(true);
  };

  const handleSaveTherapist = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      await axios.put(
        `${API_BASE_URL}/admin/provider-permissions/${selectedTherapist.id}`,
        editForm,
        { headers: { 'x-auth-token': token } }
      );
      
      setSuccess(`Updated permissions for ${selectedTherapist.name}`);
      setEditDialogOpen(false);
      fetchTherapistPermissions();
      fetchUsageStats();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError(err.response?.data?.msg || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkEdit = async () => {
    if (selectedTherapists.length === 0) {
      setError('No therapists selected');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await axios.put(
        `${API_BASE_URL}/admin/provider-permissions/bulk`,
        {
          therapistIds: selectedTherapists,
          updates: bulkEditForm
        },
        { headers: { 'x-auth-token': token } }
      );
      
      const result = response.data.results;
      setSuccess(`Updated ${result.successful.length} therapists`);
      if (result.failed.length > 0) {
        setError(`Failed to update ${result.failed.length} therapists`);
      }
      
      setBulkEditDialogOpen(false);
      setSelectedTherapists([]);
      fetchTherapistPermissions();
      fetchUsageStats();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error bulk updating:', err);
      setError(err.response?.data?.msg || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const filteredTherapists = therapists.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculateMonthlyCost = (providers) => {
    if (!stats) return 0;
    
    let totalCost = 0;
    providers.forEach(provider => {
      const avgCharsPerUser = 160000; // Estimate: 160k chars/month per therapist
      totalCost += avgCharsPerUser * (PROVIDERS[provider]?.cost || 0) / 1000;
    });
    
    return totalCost.toFixed(2);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Provider Permissions Management
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon color="primary" />
                  <Box>
                    <Typography variant="h6">{stats.totalTherapists}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Therapists
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon color="success" />
                  <Box>
                    <Typography variant="h6">
                      {stats.permissionStats?.canSelectProvider || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Can Select Provider
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon color="info" />
                  <Box>
                    <Typography variant="h6">
                      {stats.preferredProviders?.google || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Using Google TTS
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoneyIcon color="warning" />
                  <Box>
                    <Typography variant="h6">
                      {stats.preferredProviders?.elevenlabs || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Using ElevenLabs
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Actions Bar */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search therapists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchTherapistPermissions();
                fetchUsageStats();
              }}
            >
              Refresh
            </Button>
            
            <Button
              variant="contained"
              startIcon={<GroupIcon />}
              onClick={() => setBulkEditDialogOpen(true)}
              disabled={selectedTherapists.length === 0}
            >
              Bulk Edit ({selectedTherapists.length})
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Therapist Table */}
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedTherapists.length > 0 && 
                    selectedTherapists.length < filteredTherapists.length
                  }
                  checked={
                    filteredTherapists.length > 0 &&
                    selectedTherapists.length === filteredTherapists.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTherapists(filteredTherapists.map(t => t.id));
                    } else {
                      setSelectedTherapists([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>Therapist</TableCell>
              <TableCell>Allowed Providers</TableCell>
              <TableCell align="center">Can Select</TableCell>
              <TableCell>Daily Limit</TableCell>
              <TableCell>Monthly Limit</TableCell>
              <TableCell align="center">Override Interface</TableCell>
              <TableCell>Est. Monthly Cost</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTherapists.map(therapist => (
              <TableRow 
                key={therapist.id}
                hover
                selected={selectedTherapists.includes(therapist.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedTherapists.includes(therapist.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTherapists([...selectedTherapists, therapist.id]);
                      } else {
                        setSelectedTherapists(selectedTherapists.filter(id => id !== therapist.id));
                      }
                    }}
                  />
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {therapist.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {therapist.email}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {therapist.allowedProviders?.map(provider => (
                      <Chip
                        key={provider}
                        label={PROVIDERS[provider]?.name || provider}
                        size="small"
                        sx={{
                          bgcolor: PROVIDERS[provider]?.color + '20',
                          color: PROVIDERS[provider]?.color
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>
                
                <TableCell align="center">
                  <Chip
                    label={therapist.providerPermissions?.canSelectProvider ? 'Yes' : 'No'}
                    size="small"
                    color={therapist.providerPermissions?.canSelectProvider ? 'success' : 'default'}
                    variant={therapist.providerPermissions?.canSelectProvider ? 'filled' : 'outlined'}
                  />
                </TableCell>
                
                <TableCell>
                  ${therapist.providerPermissions?.costLimit?.daily || 10}
                </TableCell>
                
                <TableCell>
                  ${therapist.providerPermissions?.costLimit?.monthly || 200}
                </TableCell>
                
                <TableCell align="center">
                  <Chip
                    label={therapist.providerPermissions?.overrideInterfaceProvider ? 'Yes' : 'No'}
                    size="small"
                    color={therapist.providerPermissions?.overrideInterfaceProvider ? 'warning' : 'default'}
                    variant={therapist.providerPermissions?.overrideInterfaceProvider ? 'filled' : 'outlined'}
                  />
                </TableCell>
                
                <TableCell>
                  ${calculateMonthlyCost(therapist.allowedProviders || [])}
                </TableCell>
                
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEditTherapist(therapist)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Individual Therapist Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Provider Permissions
          {selectedTherapist && (
            <Typography variant="body2" color="text.secondary">
              {selectedTherapist.name} ({selectedTherapist.email})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Allowed Providers
            </Typography>
            <FormGroup row sx={{ mb: 2 }}>
              {Object.entries(PROVIDERS).map(([key, provider]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={editForm.allowedProviders.includes(key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditForm({
                            ...editForm,
                            allowedProviders: [...editForm.allowedProviders, key]
                          });
                        } else {
                          setEditForm({
                            ...editForm,
                            allowedProviders: editForm.allowedProviders.filter(p => p !== key)
                          });
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      {provider.name}
                      <Typography variant="caption" display="block" color="text.secondary">
                        ${provider.cost}/1k chars
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
            
            <Divider sx={{ my: 2 }} />
            
            <FormGroup sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.canSelectProvider}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      canSelectProvider: e.target.checked
                    })}
                  />
                }
                label="Can Select Preferred Provider"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
                Allows therapist to choose which provider to use
              </Typography>
            </FormGroup>
            
            <FormGroup sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.overrideInterfaceProvider}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      overrideInterfaceProvider: e.target.checked
                    })}
                  />
                }
                label="Can Override Interface Provider"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
                Allows overriding interface-configured provider (e.g., audio export)
              </Typography>
            </FormGroup>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Cost Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Daily Limit ($)"
                  type="number"
                  size="small"
                  value={editForm.costLimit.daily}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    costLimit: {
                      ...editForm.costLimit,
                      daily: parseFloat(e.target.value) || 0
                    }
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Monthly Limit ($)"
                  type="number"
                  size="small"
                  value={editForm.costLimit.monthly}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    costLimit: {
                      ...editForm.costLimit,
                      monthly: parseFloat(e.target.value) || 0
                    }
                  })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTherapist}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog
        open={bulkEditDialogOpen}
        onClose={() => setBulkEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Bulk Edit Provider Permissions
          <Typography variant="body2" color="text.secondary">
            Updating {selectedTherapists.length} therapist(s)
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              These settings will be applied to all selected therapists
            </Alert>
            
            <Typography variant="subtitle2" gutterBottom>
              Allowed Providers
            </Typography>
            <FormGroup row sx={{ mb: 2 }}>
              {Object.entries(PROVIDERS).map(([key, provider]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={bulkEditForm.allowedProviders.includes(key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBulkEditForm({
                            ...bulkEditForm,
                            allowedProviders: [...bulkEditForm.allowedProviders, key]
                          });
                        } else {
                          setBulkEditForm({
                            ...bulkEditForm,
                            allowedProviders: bulkEditForm.allowedProviders.filter(p => p !== key)
                          });
                        }
                      }}
                    />
                  }
                  label={provider.name}
                />
              ))}
            </FormGroup>
            
            <Divider sx={{ my: 2 }} />
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkEditForm.canSelectProvider}
                    onChange={(e) => setBulkEditForm({
                      ...bulkEditForm,
                      canSelectProvider: e.target.checked
                    })}
                  />
                }
                label="Can Select Preferred Provider"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkEditForm.overrideInterfaceProvider}
                    onChange={(e) => setBulkEditForm({
                      ...bulkEditForm,
                      overrideInterfaceProvider: e.target.checked
                    })}
                  />
                }
                label="Can Override Interface Provider"
              />
            </FormGroup>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Cost Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Daily Limit ($)"
                  type="number"
                  size="small"
                  value={bulkEditForm.costLimit.daily}
                  onChange={(e) => setBulkEditForm({
                    ...bulkEditForm,
                    costLimit: {
                      ...bulkEditForm.costLimit,
                      daily: parseFloat(e.target.value) || 0
                    }
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Monthly Limit ($)"
                  type="number"
                  size="small"
                  value={bulkEditForm.costLimit.monthly}
                  onChange={(e) => setBulkEditForm({
                    ...bulkEditForm,
                    costLimit: {
                      ...bulkEditForm.costLimit,
                      monthly: parseFloat(e.target.value) || 0
                    }
                  })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleBulkEdit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Apply to {selectedTherapists.length} Therapists
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderPermissionsManager;